import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import PackingSlip from "@/pages/PackingSlip";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminCatalog from "@/pages/admin/AdminCatalog";
import AdminColorGovernance from "@/pages/admin/AdminColorGovernance";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminSupplierDetail from "@/pages/admin/AdminSupplierDetail";
import AdminKnowledge from "@/pages/admin/AdminKnowledge";
import DealerKnowledge from "@/pages/dealer/DealerKnowledge";
import DealerPricing from "@/pages/dealer/DealerPricing";
import PricingSetup from "@/pages/dealer/PricingSetup";
import DealerInventory from "@/pages/dealer/DealerInventory";
import DealerShipments from "@/pages/dealer/DealerShipments";
import DealerSourcing from "@/pages/dealer/DealerSourcing";
import AdminTestData from "@/pages/admin/AdminTestData";
import DealerOrders from "@/pages/dealer/DealerOrders";
import AdminMedia from "@/pages/admin/AdminMedia";
import MediaLibrary from "@/pages/dealer/MediaLibrary";
import DealerOrderDetail from "@/pages/dealer/DealerOrderDetail";
import DealerContainerOrder from "@/pages/dealer/DealerContainerOrder";
import Login from "@/pages/Login";
import SetPassword from "@/pages/SetPassword";
import SetupPassword from "@/pages/SetupPassword";
import AdminShell from "@/layouts/AdminShell";
import DealerShell from "@/layouts/DealerShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import DealersList from "@/pages/admin/DealersList";
import DealerDetail from "@/pages/admin/DealerDetail";
import Products from "@/pages/admin/Products";
import ProductDetail from "@/pages/admin/ProductDetail";
import CollectionDetail from "@/pages/admin/CollectionDetail";
import NewDesignWizard from "@/pages/admin/NewDesignWizard";
import Inventory from "@/pages/admin/Inventory";
import Containers from "@/pages/admin/Containers";
import ContainerDetail from "@/pages/admin/ContainerDetail";
import DealerDashboard from "@/pages/dealer/DealerDashboard";
import Discover from "@/pages/dealer/Discover";
import DealerCatalogDetail from "@/pages/dealer/DealerCatalogDetail";
import Reservations from "@/pages/dealer/Reservations";
import Projects from "@/pages/dealer/Projects";
import ProjectDetail from "@/pages/dealer/ProjectDetail";
import Team from "@/pages/dealer/Team";
import DealerProfile from "@/pages/dealer/DealerProfile";
import Notifications from "@/pages/dealer/Notifications";
import DealerSamples from "@/pages/dealer/DealerSamples";
import AdminReservations from "@/pages/admin/AdminReservations";
import AdminSamples from "@/pages/admin/AdminSamples";
import AdminSourcing from "@/pages/admin/AdminSourcing";
import AdminEmailLog from "@/pages/admin/AdminEmailLog";
import AdminCertificates from "@/pages/admin/AdminCertificates";
import AdminCustomStudio from "@/pages/admin/AdminCustomStudio";
import Certificate from "@/pages/dealer/Certificate";
import CustomStudio from "@/pages/dealer/CustomStudio";
import CustomStudioDetail from "@/pages/dealer/CustomStudioDetail";
import Proposals from "@/pages/dealer/Proposals";
import ProposalView from "@/pages/dealer/ProposalView";
import Copilot from "@/pages/dealer/Copilot";
import DealerReports from "@/pages/dealer/DealerReports";
import AdminReports from "@/pages/admin/AdminReports";
import AdminVoiceTelemetry from "@/pages/admin/AdminVoiceTelemetry";
import AdminPartnerProgram from "@/pages/admin/AdminPartnerProgram";
import PublicProposal from "@/pages/PublicProposal";
import { Loader2 } from "lucide-react";

function RootRedirect() {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.force_password_change) return <Navigate to="/set-password" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/dealer"} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/setup-password" element={<SetupPassword />} />
            <Route path="/p/:token" element={<PublicProposal />} />
            <Route
              path="/set-password"
              element={
                <ProtectedRoute>
                  <SetPassword />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<RootRedirect />} />

            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="partner-program" element={<AdminPartnerProgram />} />
              <Route path="dealers" element={<DealersList />} />
              <Route path="dealers/:id" element={<DealerDetail />} />
              <Route path="products" element={<Products />} />
              <Route path="products/new" element={<NewDesignWizard />} />
              <Route path="products/:id" element={<ProductDetail />} />
              <Route path="collections/:code" element={<CollectionDetail />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="containers" element={<Containers />} />
              <Route path="containers/:id" element={<ContainerDetail />} />
              <Route path="reservations" element={<AdminReservations />} />
              <Route path="samples" element={<AdminSamples />} />
              <Route path="sourcing" element={<AdminSourcing />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/:id" element={<AdminOrderDetail />} />
              <Route path="pricing" element={<AdminPricing />} />
              <Route path="catalog" element={<AdminCatalog />} />
              <Route path="catalog/governance" element={<AdminColorGovernance />} />
              <Route path="suppliers" element={<AdminSuppliers />} />
              <Route path="suppliers/:id" element={<AdminSupplierDetail />} />
              <Route path="knowledge" element={<AdminKnowledge />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="voice-telemetry" element={<AdminVoiceTelemetry />} />
              <Route path="orders/:id/packing-slip" element={<PackingSlip admin />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="email-log" element={<AdminEmailLog />} />
              <Route path="test-data" element={<AdminTestData />} />
              <Route path="certificates" element={<AdminCertificates />} />
              <Route path="custom-studio" element={<AdminCustomStudio />} />
              <Route path="media" element={<AdminMedia />} />
            </Route>

            <Route
              path="/dealer"
              element={
                <ProtectedRoute role="dealer_user">
                  <DealerShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<DealerDashboard />} />
              <Route path="discover" element={<Discover />} />
              <Route path="copilot" element={<Copilot />} />
              <Route path="discover/:id" element={<DealerCatalogDetail />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              <Route path="proposals" element={<Proposals />} />
              <Route path="proposals/:id/view" element={<ProposalView />} />
              <Route path="pricing" element={<DealerPricing />} />
              <Route path="pricing/setup" element={<PricingSetup />} />
              <Route path="inventory" element={<DealerInventory />} />
              <Route path="shipments" element={<DealerShipments />} />
              <Route path="sourcing" element={<DealerSourcing />} />
              <Route path="team" element={<Team />} />
              <Route path="profile" element={<DealerProfile />} />
              <Route path="reservations" element={<Reservations />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="samples" element={<DealerSamples />} />
              <Route path="orders" element={<DealerOrders />} />
              <Route path="orders/:id" element={<DealerOrderDetail />} />
              <Route path="container-order" element={<DealerContainerOrder />} />
              <Route path="certificate" element={<Certificate />} />
              <Route path="custom-studio" element={<CustomStudio />} />
              <Route path="media" element={<MediaLibrary />} />
              <Route path="custom-studio/:id" element={<CustomStudioDetail />} />
              <Route path="reports" element={<DealerReports />} />
              <Route path="library" element={<DealerKnowledge />} />
              <Route path="orders/:id/packing-slip" element={<PackingSlip />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#FFFFFF",
              border: "1px solid #E6E4E0",
              color: "#1A1A18",
              borderRadius: "0.375rem",
            },
          }}
        />
      </AuthProvider>
    </div>
  );
}

export default App;
